// Pull to refresh on the four tabs (Overview, Activity, Insights, Budget), built for the installed
// PWA on iPhone Safari: touch events, the page's own bounce held back (overscroll-behavior, and a
// cancelled touchmove where that is not supported), and a spinner that follows the finger with an
// elastic resistance, scales and turns in as it is pulled, snaps to a holding spot while loading,
// stays at least MIN_SPIN_MS so it never flashes, then eases back. It never starts mid-list (only
// with the page scrolled to the very top), on a sheet, during drag and drop, from a grip or a field,
// or on a detail screen. Refreshing only reloads (GET); nothing is written.
//
// Also the silent background refresh: every 45 seconds while the app is visible and nothing is
// open, unless the last load was under 30 seconds ago.
//
// This file is the pure part (tested in test/pull.test.ts); web/src/pull-input.ts wires it to the DOM.

export const PULL = {
  /** How far (after resistance, in px) a pull must go to refresh. */
  THRESHOLD: 64,
  /** The most the spinner ever moves: the resistance curve approaches it and never passes it. */
  MAX: 120,
  /** Where the spinner waits while loading. */
  HOLD: 56,
  /** Finger movement before a pull starts, so a tap or a sideways swipe is not a pull. */
  START_SLOP: 6,
  /** The spinner shows at least this long once a refresh starts. */
  MIN_SPIN_MS: 400,
} as const;

export const BACKGROUND = { INTERVAL_MS: 45_000, MIN_AGE_MS: 30_000 } as const;

/**
 * The spinner distance for a finger movement of `dy` px down: elastic, so it follows closely at
 * first and stiffens as it goes (MAX * dy / (dy + MAX)), never past MAX.
 */
export function pullDistance(dy: number): number {
  if (!(dy > 0)) return 0;
  return (PULL.MAX * dy) / (dy + PULL.MAX);
}

/** The finger movement that reaches a spinner distance (the inverse of pullDistance). */
export const fingerFor = (distance: number) => (distance >= PULL.MAX ? Infinity : (PULL.MAX * distance) / (PULL.MAX - distance));

/**
 * How the spinner looks at a distance: it slides down, grows from half size and fades in up to the
 * threshold, and turns with the pull, so it never just appears.
 */
export function indicatorStyle(distance: number): { y: number; scale: number; rotate: number; opacity: number; ready: boolean } {
  const d = Math.max(0, Math.min(PULL.MAX, distance));
  const p = Math.min(1, d / PULL.THRESHOLD);
  return { y: round(d), scale: round(0.5 + 0.5 * p), rotate: round(d * 3), opacity: round(p), ready: d >= PULL.THRESHOLD };
}

const round = (n: number) => Math.round(n * 100) / 100;

/** How long the spinner must still show after loading finished, so it is up at least MIN_SPIN_MS. */
export const remainingSpin = (startedAt: number, now: number) => Math.max(0, PULL.MIN_SPIN_MS - (now - startedAt));

/** The silent background refresh: visible, nothing open or loading, and the data is old enough. */
export function shouldBackgroundRefresh(s: { visible: boolean; busy: boolean; lastLoaded: number; now: number }): boolean {
  return s.visible && !s.busy && s.now - s.lastLoaded >= BACKGROUND.MIN_AGE_MS;
}

export class PullState {
  private startY: number | null = null;
  private distance = 0;
  private pulling = false;

  /** A finger went down. `allowed`: this screen may pull now; `atTop`: scrolled to the very top. */
  start(y: number, atTop: boolean, allowed: boolean) {
    this.reset();
    if (allowed && atTop) this.startY = y;
  }

  /**
   * The finger moved. Returns the spinner distance and whether to cancel the event (hold back
   * the page's own bounce). Moving up first, or the page leaving the top, gives up for this touch.
   */
  move(y: number, atTop: boolean): { distance: number; prevent: boolean } {
    if (this.startY === null) return { distance: 0, prevent: false };
    const dy = y - this.startY;
    if (!this.pulling) {
      if (dy < -PULL.START_SLOP || !atTop) return this.giveUp();
      if (dy < PULL.START_SLOP) return { distance: 0, prevent: false };
      this.pulling = true;
    }
    this.distance = pullDistance(dy - PULL.START_SLOP);
    return { distance: this.distance, prevent: true };
  }

  /** The finger lifted (or the touch was cancelled): refresh when pulled far enough. */
  end(cancelled = false): 'refresh' | 'none' {
    const go = this.pulling && !cancelled && this.distance >= PULL.THRESHOLD;
    this.reset();
    return go ? 'refresh' : 'none';
  }

  private giveUp() {
    this.reset();
    return { distance: 0, prevent: false };
  }

  private reset() {
    this.startY = null;
    this.distance = 0;
    this.pulling = false;
  }
}
